import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  Search,
  MapPin,
  Banknote,
  Clock,
  Calendar,
  CheckCircle2,
  ArrowUpRight,
  RefreshCw,
} from "lucide-react"
import {
  MappedPublicJob,
  fetchPublicJobs,
  getAppliedJobIds,
} from "./recruitment.api"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

export default function RecruitmentPage() {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<MappedPublicJob[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [appliedIds, setAppliedIds] = useState<string[]>([])

  // HRM Filters
  const [search, setSearch] = useState<string>("")
  const [block, setBlock] = useState<string>("_all")
  const [timeRange, setTimeRange] = useState<string>("all")
  const [salaryMin, setSalaryMin] = useState<string>("all")

  useEffect(() => {
    let mounted = true
    setIsLoading(true)
    fetchPublicJobs("TUTIMI")
      .then(data => {
        if (mounted) setJobs(data)
      })
      .finally(() => {
        if (mounted) setIsLoading(false)
      })
    setAppliedIds(getAppliedJobIds())
    return () => {
      mounted = false
    }
  }, [])

  const filteredJobs = useMemo(() => {
    const now = new Date()
    const daysLimit =
      timeRange === "7" ? 7 : timeRange === "30" ? 30 : undefined

    return jobs.filter(job => {
      if (job.status !== "published") return false

      const matchSearch = search.trim()
        ? job.title.toLowerCase().includes(search.trim().toLowerCase()) ||
          job.location.toLowerCase().includes(search.trim().toLowerCase())
        : true

      const matchBlock = block === "_all" ? true : job.block === block

      const minSalThreshold = salaryMin === "all" ? 0 : Number(salaryMin)
      const jobMaxSal = job.salary_max_raw || job.salary_min_raw || 0
      const matchSalary = jobMaxSal >= minSalThreshold

      let matchTime = true
      if (daysLimit && job.created_at) {
        const createdDate = new Date(job.created_at)
        if (!Number.isNaN(createdDate.getTime())) {
          const diffDays = Math.floor(
            (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
          )
          matchTime = diffDays <= daysLimit
        }
      }

      return matchSearch && matchBlock && matchSalary && matchTime
    })
  }, [jobs, search, block, timeRange, salaryMin])

  const handleResetFilters = () => {
    setSearch("")
    setBlock("_all")
    setTimeRange("all")
    setSalaryMin("all")
  }

  const hasActiveFilters =
    search.trim() !== "" ||
    block !== "_all" ||
    timeRange !== "all" ||
    salaryMin !== "all"

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#142d4f]">
      <Helmet>
        <title>Tuyển Dụng – TuTiMi Coffee & Tea</title>
        <meta
          name="description"
          content="Cơ hội việc làm tại hệ thống cửa hàng TuTiMi Coffee & Tea. Ứng tuyển nhanh vị trí Phục vụ, Pha chế, Thu ngân."
        />
      </Helmet>

      {/* SIMPLE FLAT HERO SECTION */}
      <section className="bg-[#1f4171] text-white py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl space-y-3">
            <Badge className="bg-[#2ddac0] text-[#142d4f] uppercase tracking-wider px-3 py-1">
              TuTiMi Careers
            </Badge>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              Gia Nhập Đội Ngũ TuTiMi
            </h1>
            <p className="text-sm sm:text-base text-white/80 leading-relaxed">
              Cùng TuTiMi mang đến những ly trà sữa và cà phê tươi mới mỗi ngày
              trong môi trường F&B trẻ trung, thân thiện và năng động.
            </p>
          </div>
        </div>
      </section>

      {/* FLAT MODERN JOB BOARD SECTION (shadcn/ui components) */}
      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Flat Filter Bar using shadcn Input, Select & Button */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
            {/* Search Input */}
            <div className="sm:col-span-2 lg:col-span-5 relative">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Tìm kiếm theo tên công việc..."
                aria-label="Tìm kiếm theo tên công việc"
                className="pl-11 h-12"
              />
            </div>

            {/* Block Filter (shadcn Select) */}
            <div className="lg:col-span-2">
              <Select value={block} onValueChange={setBlock}>
                <SelectTrigger className="h-12" aria-label="Theo khối">
                  <SelectValue placeholder="Tất cả khối" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tất cả khối</SelectItem>
                  <SelectItem value="store">Khối Cửa hàng</SelectItem>
                  <SelectItem value="office">Khối Văn phòng</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Filter (shadcn Select) */}
            <div className="lg:col-span-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="h-12" aria-label="Theo thời gian">
                  <SelectValue placeholder="Tất cả thời gian" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả thời gian</SelectItem>
                  <SelectItem value="7">7 ngày gần đây</SelectItem>
                  <SelectItem value="30">30 ngày gần đây</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Salary Filter (shadcn Select) */}
            <div className="lg:col-span-3 flex gap-2">
              <Select value={salaryMin} onValueChange={setSalaryMin}>
                <SelectTrigger className="h-12" aria-label="Theo mức lương">
                  <SelectValue placeholder="Tất cả mức lương" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả mức lương</SelectItem>
                  <SelectItem value="5000000">Từ 5 triệu</SelectItem>
                  <SelectItem value="10000000">Từ 10 triệu</SelectItem>
                  <SelectItem value="15000000">Từ 15 triệu</SelectItem>
                  <SelectItem value="20000000">Từ 20 triệu</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleResetFilters}
                  className="shrink-0 h-12 bg-[#e2e8f0] hover:bg-[#cbd5e1]"
                >
                  Xóa lọc
                </Button>
              )}
            </div>
          </div>

          {/* Flat Job Cards List using shadcn Card, Badge & Button */}
          <div className="space-y-3">
            {isLoading ? (
              <Card className="py-16 text-center">
                <CardContent className="p-0">
                  <RefreshCw
                    size={28}
                    className="mx-auto animate-spin text-[#1f4171] mb-3"
                    aria-hidden="true"
                  />
                  <p className="text-sm font-semibold text-[#1f4171]">
                    Đang tải danh sách công việc...
                  </p>
                </CardContent>
              </Card>
            ) : filteredJobs.length === 0 ? (
              <Card className="py-14 px-4 text-center">
                <CardContent className="p-0 space-y-3">
                  <p className="text-base font-bold text-[#1f4171]">
                    Không tìm thấy công việc phù hợp
                  </p>
                  {hasActiveFilters && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleResetFilters}
                    >
                      Hiển thị tất cả
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredJobs.map(job => {
                const isApplied = appliedIds.includes(job.uuid)
                const detailUrl = `/recruitment/${job.uuid}`
                return (
                  <Card
                    key={job.uuid}
                    onClick={() => navigate(detailUrl)}
                    className="group hover:bg-[#eef3f9] transition-colors duration-150 cursor-pointer"
                  >
                    <CardContent className="p-6 sm:p-7">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                        {/* Left: Title & Essential Metadata */}
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge
                              variant="secondary"
                              className="group-hover:bg-white"
                            >
                              {job.block_label}
                            </Badge>
                            {job.tags.map(tag => (
                              <Badge
                                key={tag}
                                variant="muted"
                                className="group-hover:bg-white"
                              >
                                <Clock size={12} aria-hidden="true" />
                                {tag}
                              </Badge>
                            ))}
                            {isApplied && (
                              <Badge variant="mint">
                                <CheckCircle2 size={12} aria-hidden="true" />
                                Đã ứng tuyển
                              </Badge>
                            )}
                          </div>

                          <h2 className="text-xl sm:text-2xl font-bold text-[#1f4171] group-hover:text-[#ff7d3c] transition-colors duration-150">
                            <Link
                              to={detailUrl}
                              onClick={e => e.stopPropagation()}
                            >
                              {job.title}
                            </Link>
                          </h2>

                          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs sm:text-sm text-slate-600">
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin
                                size={15}
                                className="text-[#1f4171]"
                                aria-hidden="true"
                              />
                              {job.location}
                            </span>
                            {job.recruitment_period !== "-" && (
                              <span className="inline-flex items-center gap-1.5">
                                <Calendar
                                  size={15}
                                  className="text-[#1f4171]"
                                  aria-hidden="true"
                                />
                                Hạn nộp hồ sơ: {job.recruitment_period}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right: Salary & Clean Flat Actions */}
                        <div
                          className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-end justify-between gap-3 shrink-0"
                          onClick={e => e.stopPropagation()}
                        >
                          <Badge
                            variant="mint"
                            className="px-3.5 py-1.5 text-sm sm:text-base rounded-lg"
                          >
                            <Banknote size={17} aria-hidden="true" />
                            <span>{job.salary_display}</span>
                          </Badge>

                          <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                              asChild
                              variant="secondary"
                              className="flex-1 sm:flex-initial group-hover:bg-white hover:!bg-[#1f4171] hover:!text-white"
                            >
                              <Link to={detailUrl}>
                                <span>Chi tiết</span>
                                <ArrowUpRight size={15} aria-hidden="true" />
                              </Link>
                            </Button>
                            <Button
                              asChild
                              variant="accent"
                              className="flex-1 sm:flex-initial"
                            >
                              <Link to={`${detailUrl}#apply`}>Ứng tuyển</Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
